import {
    createPanoramaFlight,
    deletePanoramaFlight,
    updatePanoramaFlight,
} from "features/pubsub/entityActions/flights/panorama"
import { ModalNames } from "features/modals/types"
import { useContext, useEffect } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveSidePanelModal } from "panels/content/side-panel/slice"
import { PanoramaContainerWithBoundary } from "./PanoramaContainer"
import { MapPortalContext } from "features/map/layers/MapPortalContext"
import { PluginRegistry } from "plugins/plugin-registry"
import * as Icons from "@nova/component-icons"
import { PanoramaFlight } from "@nova/protobuf"
import {
    selectPanoramaFlightById,
    selectPanoramaFlights,
} from "features/entity-caching/flights/selectors"
import { zoomToPanorama } from "features/map/inspector-panel/panoramas/module"
import { SidePanelLayerType } from "@nova/constants"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { InspectorPanelPanoramaLayer } from "features/map/inspector-panel/panoramas/InspectorPanelPanoramaLayer"
import { InspectorPanelPanoramaData } from "features/map/inspector-panel/panoramas/InspectorPanelPanoramaData"
import { getGenericUploadFlowActiveFeatureSidePanelComponent } from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { PanoramaMultifileUpload } from "./PanoramaMultifileUpload"
import { usePanoramaStatusAndProgress } from "panels/content/side-panel/progress/Panorama"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { PanoramaAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/PanoramaAssetsThumbnail"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import { getUsePanoramaListItemActions } from "panels/content/side-panel/generics/genericListItemActions"
import { getUsePanoramaMapNodes } from "features/map/map-nodes/usePanoramaMapNodes"

export function registerPanoramaPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "panorama",
            displayName: "Panorama",
            sidePanelLayerType: SidePanelLayerType.Panorama,
            PrimaryIcon: Icons.PanoramaPlugin,
            ImageIconUrl: "/images/plugins/layers/panorama.svg",
            updateAction: updatePanoramaFlight,
            deleteAction: deletePanoramaFlight,
            confirmDelete: () => true,
            protobufMessage: PanoramaFlight,
            selectAllEntities: selectPanoramaFlights,
            selectSingleEntity: selectPanoramaFlightById,
            useUploadStatusAndProgress: usePanoramaStatusAndProgress,
            zoomTo: zoomToPanorama,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                useActions: getUsePanoramaListItemActions(spec),
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: PanoramaAssetsThumbnail,
            }),
        }))
        .implement("side-panel-active-feature-plugin", () => ({
            SidePanelComponent: getGenericUploadFlowActiveFeatureSidePanelComponent({
                MultifileUploadComponent: PanoramaMultifileUpload,
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelPanoramaLayer,
                DataComponent: InspectorPanelPanoramaData,
            }),
        }))
        .implement("ui-controller", () => ({
            Controller: () => {
                const activeSidePanelModal = useAppSelector(selectActiveSidePanelModal())
                const { register, unregister } = useContext(MapPortalContext)
                useEffect(() => {
                    if (activeSidePanelModal?.layerContentType === "pano") {
                        register("panoContainer", PanoramaContainerWithBoundary, {
                            fullscreen: true,
                        })
                    } else {
                        unregister("panoContainer")
                    }
                }, [activeSidePanelModal, register, unregister])

                return <></>
            },
        }))
        .implement("feature-plugin", () => ({
            createAction: createPanoramaFlight,
            sampleDataModalName: ModalNames.TryPanoramaModal,
        }))
        .implement("map-node-plugin", (spec) => ({
            useMapNodes: getUsePanoramaMapNodes(spec),
        }))
}
